// Session Plan page fit (NP-3B).
//
// The plan is shrunk with ONE uniform CSS scale when its natural height
// exceeds the usable A4 height (see SessionPlanPreview). Shrinking frees
// horizontal space, so the content may be laid out WIDER first
// (width / k) and then scaled: text reflows into the freed width and the
// page can often be scaled less. This helper chooses that layout width.
//
// Pure: the caller supplies `heightAtWidthFactor(k)`, which lays the
// content out at width / k and returns its natural height (px).
//
// Guarantees:
//   • scale is never below the plain height-only scale (usable / natural)
//     and never above 1;
//   • with no overflow the page is left alone (scale 1, width factor 1);
//   • the curve "scale vs layout width" can rise then fall (text reflow
//     vs diagrams growing with width), so a coarse sweep is followed by a
//     local refinement around the best sample.

export const FIT_SAMPLES = 12;
export const FIT_REFINE_STEPS = 6;
const EPS = 0.0005;

export function choosePlanFit({ usable, natural, heightAtWidthFactor, samples = FIT_SAMPLES, refineSteps = FIT_REFINE_STEPS }) {
  if (!(usable > 0) || !(natural > usable + 0.5)) {
    return { scale: 1, widthFactor: 1, baseScale: 1 };
  }
  const baseScale = usable / natural;
  // Layout at width / k, scaled by min(k, usable / height): at k the page
  // can never be scaled above k (it would overflow the page width).
  const scaleAt = (k) => Math.min(k, usable / heightAtWidthFactor(k));

  let bestK = 1;
  let bestScale = baseScale;
  const consider = (k) => {
    if (!(k > baseScale) || k >= 1) return;
    const s = scaleAt(k);
    if (s > bestScale + EPS) {
      bestScale = s;
      bestK = k;
    }
  };
  for (let i = 0; i < samples; i++) consider(baseScale + ((1 - baseScale) * i) / samples);
  let span = (1 - baseScale) / samples;
  for (let i = 0; i < refineSteps; i++) {
    span /= 2;
    const centre = bestK;
    consider(centre - span);
    consider(centre + span);
  }
  return { scale: Math.min(1, bestScale), widthFactor: bestK, baseScale };
}

// Left offset (px) that centres the scaled content inside `width`.
export function centredOffset({ width, widthFactor, scale }) {
  const used = (width / widthFactor) * scale;
  return Math.max(0, (width - used) / 2);
}
